// src/pages/employeeCommission/CommissionConfigPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Package, Plus, Edit, Trash2, Download, FileSpreadsheet,
  Info, Lock, ChevronLeft, ChevronRight, Settings, CalendarDays, Clock,
} from 'lucide-react';
import {
  getEmployeeLevelConfigs,
  getProductLevelConfigs,
  deleteCommissionConfig,
  getCommissionSettings,
  saveCommissionSettings,
} from '../../services/api/employeeCommission';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import EmployeeLevelSingleModal from './components/EmployeeLevelSingleModal';
import EmployeeLevelBulkModal from './components/EmployeeLevelBulkModal';
import ProductLevelSingleModal from './components/ProductLevelSingleModal';
import ProductLevelBulkModal from './components/ProductLevelBulkModal';
import EmployeeProductLevelModal from './components/EmployeeProductLevelModal';
import ProductEmployeeLevelModal from './components/ProductEmployeeLevelModal';
import styles from './CommissionConfigPage.module.css';
import * as XLSX from 'xlsx';

const PAGE_SIZE = 25;

const TIME_RANGE_LABELS = {
  monthly:     'Monthly',
  fortnightly: 'Fortnightly',
  weekly:      'Weekly',
};

const CommissionConfigPage = () => {
  const { t } = useLocalization();
  const { error: showError, confirm } = useAlert();

  const [activeView, setActiveView] = useState('user');
  const [employeeConfigs, setEmployeeConfigs] = useState([]);
  const [productConfigs, setProductConfigs] = useState([]);
  const [totalEmployee, setTotalEmployee] = useState(0);
  const [totalProduct, setTotalProduct] = useState(0);
  const [employeePage, setEmployeePage] = useState(1);
  const [productPage, setProductPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasEmployeeConfigs, setHasEmployeeConfigs] = useState(false);

  const [modal, setModal] = useState(null);
  const [modalData, setModalData] = useState(null);

  const [cutoffDay, setCutoffDay] = useState(null); // null = End of Month; 1-28 = day
  const [cutoffSaving, setCutoffSaving] = useState(false);

  useEffect(() => {
    loadEmployeeConfigs(1);
    loadCutoffSetting();
  }, []);

  const loadCutoffSetting = async () => {
    try {
      const settings = await getCommissionSettings();
      const saved = settings.monthlyCutoffDate;
      setCutoffDay((saved && saved >= 1 && saved <= 28) ? saved : null);
    } catch {
      // ignore — default to End of Month
    }
  };

  const handleCutoffChange = async (newValue) => {
    setCutoffDay(newValue);
    setCutoffSaving(true);
    try {
      await saveCommissionSettings({ monthlyCutoffDate: newValue });
    } catch {
      showError('Failed to save cutoff date setting.');
    } finally {
      setCutoffSaving(false);
    }
  };

  useEffect(() => {
    if (activeView === 'product') {
      loadProductConfigs(1);
    }
  }, [activeView]);

  const loadEmployeeConfigs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await getEmployeeLevelConfigs({ page, limit: PAGE_SIZE });
      setEmployeeConfigs(res?.configs || []);
      setTotalEmployee(res?.total || 0);
      setEmployeePage(page);
      setHasEmployeeConfigs((res?.total || 0) > 0);
    } catch {
      setEmployeeConfigs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProductConfigs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await getProductLevelConfigs({ page, limit: PAGE_SIZE });
      setProductConfigs(res?.configs || []);
      setTotalProduct(res?.total || 0);
      setProductPage(page);
    } catch {
      setProductConfigs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = async (configId) => {
    const ok = await confirm(t('employeeCommission.confirmDelete'));
    if (!ok) return;
    try {
      await deleteCommissionConfig(configId);
      activeView === 'user' ? loadEmployeeConfigs(employeePage) : loadProductConfigs(productPage);
    } catch {
      showError(t('employeeCommission.configDeleteFailed'));
    }
  };

  const handleExportEmployees = () => {
    const data = employeeConfigs.map((c) => ({
      ID:                      c.userId,
      Name:                    c.employee?.name || '',
      'Employee #':            c.employee?.employeeNumber || '',
      'Confirm Commission':    parseFloat(c.confirmCommission).toFixed(4),
      'Create Commission':     parseFloat(c.createCommission).toFixed(4),
      'Calculation Basis':     c.calculationBasis,
      'Period':                c.timeRange || 'monthly',
      'Start Date':            c.startDate || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employee Commissions');
    XLSX.writeFile(wb, 'employee_commission_config.xlsx');
  };

  const handleExportProducts = () => {
    const data = productConfigs.map((c) => ({
      'Product ID':         c.productId,
      'Product Name':       c.product?.name || '',
      SKU:                  c.product?.sku || '',
      'Confirm Commission': parseFloat(c.confirmCommission).toFixed(4),
      'Create Commission':  parseFloat(c.createCommission).toFixed(4),
      'Calculation Basis':  c.calculationBasis,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Product Commissions');
    XLSX.writeFile(wb, 'product_commission_config.xlsx');
  };

  const openModal = (type, data = null) => {
    setModal(type);
    setModalData(data);
  };

  const closeModal = () => {
    setModal(null);
    setModalData(null);
  };

  const onModalSaved = () => {
    if (activeView === 'user') loadEmployeeConfigs(employeePage);
    else loadProductConfigs(productPage);
    getEmployeeLevelConfigs({ page: 1, limit: 1 }).then((res) => {
      setHasEmployeeConfigs((res?.total || 0) > 0);
    }).catch(() => {});
  };

  const basisLabel = (key) => {
    const map = {
      order_count:    t('employeeCommission.orderCount'),
      order_quantity: t('employeeCommission.orderQuantity'),
      order_value:    t('employeeCommission.orderValue'),
    };
    return map[key] || key;
  };

  const formatCommission = (config, field) => {
    const val = parseFloat(field === 'confirm' ? config.confirmCommission : config.createCommission).toFixed(2);
    return config.calculationBasis === 'order_value' ? `${val}%` : `LKR ${val}`;
  };

  const totalEmployeePages = Math.ceil(totalEmployee / PAGE_SIZE);
  const totalProductPages  = Math.ceil(totalProduct  / PAGE_SIZE);

  return (
    <div className={styles.pageContainer}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <h1 className={styles.pageTitle}>{t('employeeCommission.commissionConfigTitle')}</h1>
          <p className={styles.pageSubtitle}>{t('employeeCommission.commissionConfigSubtitle')}</p>
        </div>
      </div>

      {/* View Toggle */}
      <div className={styles.viewToggle}>
        <button
          className={`${styles.toggleBtn} ${activeView === 'user' ? styles.toggleBtnActive : ''}`}
          onClick={() => setActiveView('user')}
        >
          <Users size={16} />
          {t('employeeCommission.userView')}
        </button>
        <button
          className={`${styles.toggleBtn} ${activeView === 'product' ? styles.toggleBtnActive : ''}`}
          onClick={() => setActiveView('product')}
        >
          <Package size={16} />
          {t('employeeCommission.productView')}
        </button>
      </div>

      {/* Info banner for user view */}
      {activeView === 'user' && (
        <div className={styles.infoBanner}>
          <Info size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <p>{t('employeeCommission.userViewNote')}</p>
        </div>
      )}

      {/* Monthly Cutoff Date Setting */}
      <div className={styles.tableCard} style={{ marginBottom: '1.25rem', padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CalendarDays size={15} style={{ color: 'var(--color-primary)' }} />
            <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--color-text-primary)' }}>
              Monthly Commission Cutoff
            </span>
          </div>
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
            Applies only to employees with monthly time range. Defines when each commission period ends.
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {cutoffSaving && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>Saving…</span>}
            <select
              value={cutoffDay === null ? '' : String(cutoffDay)}
              onChange={(e) => {
                const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                handleCutoffChange(val);
              }}
              style={{
                padding: '0.4rem 0.75rem',
                border: '1px solid var(--color-gray-300)',
                borderRadius: 8,
                fontSize: '0.875rem',
                color: 'var(--color-text-primary)',
                background: 'var(--color-bg-primary)',
                cursor: 'pointer',
              }}
            >
              <option value="">End of Month</option>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={String(d)}>{d}</option>
              ))}
            </select>
          </div>
        </div>
        {cutoffDay !== null && (
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
            Current setting: period runs from the <strong>{cutoffDay + 1}th</strong> of the previous month
            to the <strong>{cutoffDay}th</strong> of the current month.
          </p>
        )}
      </div>

      {/* ── USER VIEW ── */}
      {activeView === 'user' && (
        <div className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <h3 className={styles.tableTitle}>
              {t('employeeCommission.userView')}
              {totalEmployee > 0 && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.8125rem', fontWeight: 400, color: 'var(--color-text-tertiary)' }}>
                  ({totalEmployee})
                </span>
              )}
            </h3>
            <div className={styles.tableActions}>
              <button className={styles.btnSecondary} onClick={handleExportEmployees}>
                <Download size={15} />
                {t('employeeCommission.export')}
              </button>
              <button className={styles.btnSecondary} onClick={() => openModal('emp-bulk')}>
                <FileSpreadsheet size={15} />
                {t('employeeCommission.bulkAdd')}
              </button>
              <button className={styles.btnPrimary} onClick={() => openModal('emp-single')}>
                <Plus size={15} />
                {t('employeeCommission.addEmployee')}
              </button>
            </div>
          </div>

          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner} />
              <span>{t('employeeCommission.loading')}</span>
            </div>
          ) : employeeConfigs.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}><Users size={24} /></div>
              <p className={styles.emptyTitle}>{t('employeeCommission.noEmployeeConfigs')}</p>
              <p className={styles.emptyDesc}>{t('employeeCommission.noEmployeeConfigsDesc')}</p>
            </div>
          ) : (
            <>
              <div className={styles.tableWrapper}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>{t('employeeCommission.userId')}</th>
                      <th>{t('employeeCommission.userName')}</th>
                      <th>{t('employeeCommission.employeeNumber')}</th>
                      <th>{t('employeeCommission.calculationBasis')}</th>
                      <th>{t('employeeCommission.confirmCommission')}</th>
                      <th>{t('employeeCommission.createCommission')}</th>
                      <th>Period</th>
                      <th>{t('employeeCommission.startDateLabel')}</th>
                      <th>{t('employeeCommission.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeConfigs.map((c) => (
                      <tr key={c.id}>
                        <td style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8125rem' }}>
                          {c.userId}
                        </td>
                        <td>
                          <span style={{ fontWeight: 500 }}>{c.employee?.name || '—'}</span>
                        </td>
                        <td style={{ color: 'var(--color-text-secondary)' }}>
                          {c.employee?.employeeNumber || '—'}
                        </td>
                        <td>
                          <span className={styles.basisBadge}>{basisLabel(c.calculationBasis)}</span>
                        </td>
                        <td>
                          <span className={styles.commissionBadge}>{formatCommission(c, 'confirm')}</span>
                        </td>
                        <td>
                          <span className={styles.commissionBadge}>{formatCommission(c, 'create')}</span>
                        </td>
                        <td>
                          <span className={styles.timeRangeBadge}>
                            <Clock size={11} />
                            {TIME_RANGE_LABELS[c.timeRange] || 'Monthly'}
                          </span>
                        </td>
                        <td>
                          {c.startDate ? (
                            <span className={styles.startDateBadge}>
                              <CalendarDays size={11} />
                              {/* Show YYYY-MM-DD date only */}
                              {typeof c.startDate === 'string'
                                ? c.startDate.split('T')[0]
                                : new Date(c.startDate).toISOString().split('T')[0]}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8125rem' }}>—</span>
                          )}
                        </td>
                        <td>
                          <div className={styles.rowActions}>
                            <button
                              className={styles.btnIconSmall}
                              onClick={() => openModal('emp-product', { userId: c.userId, name: c.employee?.name })}
                              title={t('employeeCommission.viewProductCommissions')}
                            >
                              <Settings size={13} />
                              Products
                            </button>
                            <button
                              className={styles.btnIconSmall}
                              onClick={() => openModal('emp-single', c)}
                              title={t('employeeCommission.editEmployee')}
                            >
                              <Edit size={13} />
                            </button>
                            <button
                              className={styles.btnDanger}
                              onClick={() => handleDelete(c.id)}
                              title={t('employeeCommission.deleteConfig')}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalEmployeePages > 1 && (
                <div className={styles.pagination}>
                  <span>
                    Showing {(employeePage - 1) * PAGE_SIZE + 1}–{Math.min(employeePage * PAGE_SIZE, totalEmployee)} of {totalEmployee}
                  </span>
                  <div className={styles.paginationButtons}>
                    <button
                      className={styles.paginationBtn}
                      onClick={() => loadEmployeeConfigs(employeePage - 1)}
                      disabled={employeePage === 1}
                    >
                      <ChevronLeft size={14} />
                    </button>
                    {Array.from({ length: totalEmployeePages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        className={`${styles.paginationBtn} ${p === employeePage ? styles.paginationBtnActive : ''}`}
                        onClick={() => loadEmployeeConfigs(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      className={styles.paginationBtn}
                      onClick={() => loadEmployeeConfigs(employeePage + 1)}
                      disabled={employeePage === totalEmployeePages}
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── PRODUCT VIEW ── */}
      {activeView === 'product' && (
        <>
          {!hasEmployeeConfigs ? (
            <div className={styles.tableCard}>
              <div className={styles.lockedOverlay}>
                <div className={styles.lockedIcon}><Lock size={28} /></div>
                <h3 className={styles.lockedTitle}>{t('employeeCommission.productViewLockedTitle')}</h3>
                <p className={styles.lockedDesc}>{t('employeeCommission.productViewLockedMessage')}</p>
                <button className={styles.btnPrimary} onClick={() => setActiveView('user')}>
                  <Users size={15} />
                  Go to User View
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <h3 className={styles.tableTitle}>
                  {t('employeeCommission.productView')}
                  {totalProduct > 0 && (
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.8125rem', fontWeight: 400, color: 'var(--color-text-tertiary)' }}>
                      ({totalProduct})
                    </span>
                  )}
                </h3>
                <div className={styles.tableActions}>
                  <button className={styles.btnSecondary} onClick={handleExportProducts}>
                    <Download size={15} />
                    {t('employeeCommission.export')}
                  </button>
                  <button className={styles.btnSecondary} onClick={() => openModal('prod-bulk')}>
                    <FileSpreadsheet size={15} />
                    {t('employeeCommission.bulkAdd')}
                  </button>
                  <button className={styles.btnPrimary} onClick={() => openModal('prod-single')}>
                    <Plus size={15} />
                    {t('employeeCommission.addProduct')}
                  </button>
                </div>
              </div>

              {loading ? (
                <div className={styles.loadingContainer}>
                  <div className={styles.loadingSpinner} />
                  <span>{t('employeeCommission.loading')}</span>
                </div>
              ) : productConfigs.length === 0 ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}><Package size={24} /></div>
                  <p className={styles.emptyTitle}>{t('employeeCommission.noProductConfigs')}</p>
                  <p className={styles.emptyDesc}>{t('employeeCommission.noProductConfigsDesc')}</p>
                </div>
              ) : (
                <>
                  <div className={styles.tableWrapper}>
                    <table className={styles.dataTable}>
                      <thead>
                        <tr>
                          <th>{t('employeeCommission.productId')}</th>
                          <th>{t('employeeCommission.productName')}</th>
                          <th>{t('employeeCommission.sku')}</th>
                          <th>{t('employeeCommission.calculationBasis')}</th>
                          <th>{t('employeeCommission.confirmCommission')}</th>
                          <th>{t('employeeCommission.createCommission')}</th>
                          <th>{t('employeeCommission.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productConfigs.map((c) => (
                          <tr key={c.id}>
                            <td style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8125rem' }}>
                              {c.productId}
                            </td>
                            <td style={{ fontWeight: 500 }}>{c.product?.name || '—'}</td>
                            <td style={{ color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>
                              {c.product?.sku || '—'}
                            </td>
                            <td>
                              <span className={styles.basisBadge}>{basisLabel(c.calculationBasis)}</span>
                            </td>
                            <td>
                              <span className={styles.commissionBadge}>{formatCommission(c, 'confirm')}</span>
                            </td>
                            <td>
                              <span className={styles.commissionBadge}>{formatCommission(c, 'create')}</span>
                            </td>
                            <td>
                              <div className={styles.rowActions}>
                                <button
                                  className={styles.btnIconSmall}
                                  onClick={() => openModal('prod-employee', { productId: c.productId, id: c.productId, name: c.product?.name })}
                                  title={t('employeeCommission.viewEmployeeCommissions')}
                                >
                                  <Settings size={13} />
                                  Employees
                                </button>
                                <button
                                  className={styles.btnIconSmall}
                                  onClick={() => openModal('prod-single', c)}
                                  title={t('employeeCommission.editProduct')}
                                >
                                  <Edit size={13} />
                                </button>
                                <button
                                  className={styles.btnDanger}
                                  onClick={() => handleDelete(c.id)}
                                  title={t('employeeCommission.deleteConfig')}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalProductPages > 1 && (
                    <div className={styles.pagination}>
                      <span>
                        Showing {(productPage - 1) * PAGE_SIZE + 1}–{Math.min(productPage * PAGE_SIZE, totalProduct)} of {totalProduct}
                      </span>
                      <div className={styles.paginationButtons}>
                        <button
                          className={styles.paginationBtn}
                          onClick={() => loadProductConfigs(productPage - 1)}
                          disabled={productPage === 1}
                        >
                          <ChevronLeft size={14} />
                        </button>
                        {Array.from({ length: totalProductPages }, (_, i) => i + 1).map((p) => (
                          <button
                            key={p}
                            className={`${styles.paginationBtn} ${p === productPage ? styles.paginationBtnActive : ''}`}
                            onClick={() => loadProductConfigs(p)}
                          >
                            {p}
                          </button>
                        ))}
                        <button
                          className={styles.paginationBtn}
                          onClick={() => loadProductConfigs(productPage + 1)}
                          disabled={productPage === totalProductPages}
                        >
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* ── MODALS ── */}
      {modal === 'emp-single' && (
        <EmployeeLevelSingleModal
          onClose={closeModal}
          onSaved={onModalSaved}
          existingConfig={modalData}
        />
      )}
      {modal === 'emp-bulk' && (
        <EmployeeLevelBulkModal
          onClose={closeModal}
          onSaved={onModalSaved}
        />
      )}
      {modal === 'prod-single' && (
        <ProductLevelSingleModal
          onClose={closeModal}
          onSaved={onModalSaved}
          existingConfig={modalData}
        />
      )}
      {modal === 'prod-bulk' && (
        <ProductLevelBulkModal
          onClose={closeModal}
          onSaved={onModalSaved}
        />
      )}
      {modal === 'emp-product' && modalData && (
        <EmployeeProductLevelModal
          onClose={closeModal}
          employee={modalData}
        />
      )}
      {modal === 'prod-employee' && modalData && (
        <ProductEmployeeLevelModal
          onClose={closeModal}
          product={modalData}
        />
      )}
    </div>
  );
};

export default CommissionConfigPage;